import { IdSchema, TimestampSchema } from "@uzverse/core";
import { z } from "zod";
/**
 * Contracts of the vehicles package (docs/PHASE2_PLAN.md "Package 8 - vehicles"): the catalog,
 * the entities the world partition stores, the driving pose message and the REST routes.
 *
 * Conventions: meters, Y up. `yawDeg` is the heading around +Y; yaw 0 faces +Z and a positive
 * yaw turns toward +X (a model's forward axis is +Z). `speed` is meters per second.
 */
export const VEHICLE_CLASSES = [
    "city",
    "sedan",
    "suv",
    "sport",
    "van",
    "bus",
    "bike",
    "police",
    "taxi",
    "scooter",
];
export const VehicleClassSchema = z.enum(VEHICLE_CLASSES);
/** `#rrggbb`. */
export const VehicleColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const VehicleModelSchema = z.object({
    id: z.string().min(1).max(60),
    name: z.string().min(1).max(80),
    class: VehicleClassSchema,
    /** Price in credits. */
    priceCredits: z.number().int().positive(),
    topSpeedKmh: z.number().positive(),
    /** Relative acceleration rating, 1 (slow) to 10 (fast). */
    accel: z.number().min(1).max(10),
    seats: z.number().int().min(1).max(60),
    /** Files under `/world/models/<pack>/`; `lod1` is the cheaper model for distant views. */
    asset: z.object({
        pack: z.string().min(1),
        file: z.string().min(1),
        lod1: z.string().min(1).optional(),
    }),
    /** Offered paint colors (`#rrggbb`); the first one is the default. */
    colors: z.array(VehicleColorSchema).min(1),
    /** Real-brand model: hidden when `world.assets.allowBranded` is off (see `isAssetAllowed`). */
    branded: z.boolean().optional(),
    /** Old Quaternius fallback entry, removed once the vehicles package no longer needs it. */
    legacy: z.boolean().optional(),
});
export const VehiclePoseSchema = z.object({
    x: z.number().finite(),
    z: z.number().finite(),
    yawDeg: z.number().finite(),
});
export const VEHICLE_STATES = ["parked", "driving"];
export const VehicleStateSchema = z.enum(VEHICLE_STATES);
/** A vehicle as the tools return it (`vehicles.buy`, `vehicles.mine`, ...). */
export const VehicleSchema = z.object({
    id: IdSchema,
    modelId: z.string(),
    ownerUserId: IdSchema,
    color: VehicleColorSchema,
    state: VehicleStateSchema,
    pose: VehiclePoseSchema,
    driverUserId: IdSchema.optional(),
    odometerM: z.number().nonnegative(),
    boughtAt: TimestampSchema,
    /** Users the owner allowed to drive it. */
    allowedUserIds: z.array(IdSchema),
});
export const WalletSchema = z.object({
    userId: IdSchema,
    credits: z.number().int().nonnegative(),
    updatedAt: TimestampSchema,
});
export const DealershipSchema = z.object({
    id: IdSchema,
    placementId: IdSchema,
    name: z.string(),
    stockModelIds: z.array(z.string()),
});
/**
 * Realtime driving pose. Client -> gateway (`vehicle.pose` socket message, only from the
 * driver, at most 10 per second per vehicle) -> world service -> every world subscriber
 * (`vehicle.pose` server message, seq 0). Never a tool call.
 */
export const VehiclePoseMessageSchema = z.object({
    vehicleId: IdSchema,
    x: z.number().finite(),
    z: z.number().finite(),
    yawDeg: z.number().finite(),
    /** Meters per second (signed: negative = reversing). */
    speed: z.number().finite(),
});
/** Gateway REST routes (session-authenticated). */
export const VEHICLE_ROUTES = {
    /** GET -> `{ models: VehicleModel[], dealership: Dealership | null }`. */
    catalog: "/api/v1/vehicles/catalog",
    /** GET -> `{ vehicles: Vehicle[] }`: the caller's own vehicles. */
    mine: "/api/v1/vehicles/mine",
    /** GET -> `Wallet` (created with the starting balance on first read). */
    wallet: "/api/v1/wallet",
    /** POST `{ credits?, packageId? }` -> `TopUpResult` (see `WalletTopUpBodySchema`). */
    topUp: "/api/v1/wallet/top-up",
    /** GET -> `TopUpOptions`: provider, packages, limits (what the "Kredi yükle" dialog shows). */
    topUpOptions: "/api/v1/wallet/top-up/options",
    /** GET -> `TopUp`: one of the caller's top-ups (polled while a checkout is pending). */
    topUpStatus: "/api/v1/wallet/top-up/:id",
    /** GET `?limit=` -> `{ entries: WalletEntry[] }`: the caller's own ledger, newest first. */
    walletHistory: "/api/v1/wallet/history",
    /**
     * POST (no session; raw body, `stripe-signature` header) -> `{ received: true }`. Stripe's
     * `checkout.session.completed` lands here and confirms the matching top-up.
     */
    stripeWebhook: "/api/v1/wallet/stripe/webhook",
};
/** Owner-panel routes (owner-api). */
export const OWNER_WALLET_ROUTES = {
    /** GET -> `{ wallets: OwnerWallet[] }`: every wallet with the user's name. */
    list: "/owner/wallets",
    /** POST `{ userId, credits, reason }` -> `Wallet`. */
    grant: "/owner/wallets/grant",
    /** GET -> `{ topUps: OwnerTopUp[] }`: every top-up, newest first. */
    topUps: "/owner/wallets/top-ups",
    /** GET -> `TopUpOptions`; PUT `{ provider, maxPerDay? }` -> `TopUpOptions`. */
    topUpConfig: "/owner/wallets/top-up-config",
};
export const TOP_UP_PROVIDER_IDS = ["free", "stripe"];
export const TopUpProviderIdSchema = z.enum(TOP_UP_PROVIDER_IDS);
export const TOP_UP_STATUSES = ["pending", "completed", "failed"];
export const TopUpStatusSchema = z.enum(TOP_UP_STATUSES);
export const TOP_UP_CURRENCIES = ["TRY", "USD"];
export const TopUpCurrencySchema = z.enum(TOP_UP_CURRENCIES);
/** Smallest and largest single top-up, in credits. */
export const TOP_UP_MIN_CREDITS = 1_000;
export const TOP_UP_MAX_CREDITS = 1_000_000;
export const TopUpPackageSchema = z.object({
    id: z.string().regex(/^[a-z0-9-]{1,30}$/),
    credits: z.number().int().min(TOP_UP_MIN_CREDITS).max(TOP_UP_MAX_CREDITS),
});
export const DEFAULT_TOP_UP_PACKAGES = [
    { id: "p10k", credits: 10_000 },
    { id: "p50k", credits: 50_000 },
    { id: "p100k", credits: 100_000 },
];
/** Price of 1,000 credits in minor units (kuruş / cents), per currency. Used by paid providers. */
export const TopUpPricesSchema = z.object({
    TRY: z.number().int().positive(),
    USD: z.number().int().positive(),
});
export const DEFAULT_TOP_UP_PRICES = { TRY: 1000, USD: 30 };
export const TopUpSchema = z.object({
    id: IdSchema,
    userId: IdSchema,
    credits: z.number().int().positive(),
    provider: TopUpProviderIdSchema,
    status: TopUpStatusSchema,
    currency: TopUpCurrencySchema,
    /** Money charged in minor units; 0 for the free provider. */
    amountMinor: z.number().int().nonnegative(),
    packageId: z.string().optional(),
    /** Where the user pays (pending paid providers only). */
    checkoutUrl: z.string().optional(),
    createdAt: TimestampSchema,
    completedAt: TimestampSchema.optional(),
});
export const OwnerTopUpSchema = TopUpSchema.extend({
    displayName: z.string().nullable(),
    email: z.string().nullable(),
});
export const TopUpResultSchema = z.object({ topUp: TopUpSchema, wallet: WalletSchema });
export const TopUpOptionsSchema = z.object({
    provider: TopUpProviderIdSchema,
    /** True while credits cost nothing (the temporary mode). */
    free: z.boolean(),
    providers: z.array(TopUpProviderIdSchema),
    stripeConfigured: z.boolean(),
    packages: z.array(TopUpPackageSchema),
    currency: TopUpCurrencySchema,
    prices: TopUpPricesSchema,
    /** Credits one user may top up per rolling 24 hours; 0 = unlimited. */
    maxPerDay: z.number().int().nonnegative(),
    min: z.number().int().positive(),
    max: z.number().int().positive(),
});
export const WalletTopUpBodySchema = z
    .object({
    credits: z.number().int().min(TOP_UP_MIN_CREDITS).max(TOP_UP_MAX_CREDITS).optional(),
    packageId: z
        .string()
        .regex(/^[a-z0-9-]{1,30}$/)
        .optional(),
})
    .strict()
    .refine((b) => b.credits !== undefined || b.packageId !== undefined, "credits or packageId");
export const WALLET_ENTRY_KINDS = ["topup", "purchase", "sale", "grant"];
export const WalletEntryKindSchema = z.enum(WALLET_ENTRY_KINDS);
/** One line of a wallet ledger. `credits` is signed; `balance` is the balance after it. */
export const WalletEntrySchema = z.object({
    id: IdSchema,
    kind: WalletEntryKindSchema,
    credits: z.number().int(),
    balance: z.number().int().nonnegative(),
    /** Model name, grant reason or top-up provider. */
    note: z.string().optional(),
    at: TimestampSchema,
});
export const TopUpConfigBodySchema = z
    .object({
    provider: TopUpProviderIdSchema,
    maxPerDay: z.number().int().min(0).max(100_000_000).optional(),
})
    .strict();
export const OwnerWalletSchema = WalletSchema.extend({
    displayName: z.string().nullable(),
    email: z.string().nullable(),
});
export const WalletGrantBodySchema = z
    .object({
    userId: IdSchema,
    credits: z
        .number()
        .int()
        .min(-10_000_000)
        .max(10_000_000)
        .refine((n) => n !== 0, "not zero"),
    reason: z.string().trim().min(3).max(300),
})
    .strict();
/** Routes of `services/world` for vehicles (service token, audience `world`). */
export const WORLD_VEHICLE_API = {
    /** POST `{ userId, pose: VehiclePoseMessage }` -> 202 `{ accepted }`. */
    pose: "/v1/vehicles/pose",
    /** POST `{ topUpId, payload }` -> `{ status }`: a payment provider confirms a top-up. */
    walletConfirm: "/v1/wallet/confirm",
};
/** Setting key: credits every user starts with. */
export const VEHICLES_STARTING_CREDITS_KEY = "vehicles.startingCredits";
export const DEFAULT_STARTING_CREDITS = 50_000;
//# sourceMappingURL=vehicles.js.map